/**
 * Find a video file's frame rate in the browser.
 *
 * Two passes:
 *
 * 1. Exact: MP4 / MOV / M4V files (ISO base media file format) record the
 *    rate in the container. The video track's media timescale and its sample
 *    durations (mdia/mdhd and stbl/stts) give it exactly, so 30000/1001 reads
 *    as 29.97 rather than "about 30". Only the box headers and the few small
 *    boxes involved are read, through Blob.slice, so a multi-gigabyte file
 *    costs a handful of tiny reads, wherever its `moov` box sits.
 *
 * 2. Measured: for other containers, or when the container cannot be read,
 *    play about a second of the file muted and off-screen and time the frames
 *    with requestVideoFrameCallback.
 *
 * Either way the result is snapped to the nearest standard rate. Null means
 * unknown, which is recorded and shown honestly rather than guessed.
 */

const STANDARD_RATES = [23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60, 120];

/** Snap a measured rate to a standard one, or null if nothing is close. */
export function snapToStandardFrameRate(measured: number): number | null {
  if (!Number.isFinite(measured) || measured <= 0) return null;
  let best = STANDARD_RATES[0];
  for (const rate of STANDARD_RATES) {
    if (Math.abs(rate - measured) < Math.abs(best - measured)) best = rate;
  }
  // Within 1.5% — tight enough to separate 29.97 from 30 only via the median
  // below, loose enough to absorb normal decode jitter.
  return Math.abs(best - measured) / best <= 0.015 ? best : null;
}

// ---------------------------------------------------------------------------
// Pass 1: the container (ISO BMFF: MP4, MOV, M4V, 3GP)
// ---------------------------------------------------------------------------

/** Types an ISO BMFF / QuickTime file can start with. */
const LEADING_BOX_TYPES = new Set(['ftyp', 'moov', 'mdat', 'free', 'skip', 'wide', 'pnot', 'uuid', 'styp']);
/** Enough to find `moov` behind any realistic run of fragments. */
const MAX_TOP_LEVEL_BOXES = 10000;
const MAX_CHILD_BOXES = 1024;
/** 4M stts entries. A larger table is not a real video track. */
const MAX_STTS_BYTES = 32 * 1024 * 1024;
const TWO_POW_32 = 2 ** 32;

interface Box {
  type: string;
  start: number;
  /** First byte after the header. */
  payloadStart: number;
  /** One past the last byte of the box. */
  end: number;
}

const readView = async (file: Blob, start: number, end: number): Promise<DataView> => {
  const slice = file.slice(start, end);
  const buffer: ArrayBuffer = typeof slice.arrayBuffer === 'function'
    ? await slice.arrayBuffer()
    : await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(slice);
      });
  return new DataView(buffer);
};

const readFourCC = (view: DataView, offset: number): string | null => {
  let type = '';
  for (let i = 0; i < 4; i += 1) {
    const code = view.getUint8(offset + i);
    // Printable ASCII, plus the (c) sign QuickTime uses in metadata names.
    if ((code < 0x20 || code > 0x7e) && code !== 0xa9) return null;
    type += String.fromCharCode(code);
  }
  return type;
};

/** A 64-bit unsigned value, or null when it cannot be a real file offset. */
const readUint64 = (view: DataView, offset: number): number | null => {
  const value = view.getUint32(offset) * TWO_POW_32 + view.getUint32(offset + 4);
  return Number.isSafeInteger(value) ? value : null;
};

/**
 * The box header at `offset`, or null when there is no well-formed box that
 * fits before `limit`. Handles 64-bit sizes (size 1) and size 0, which means
 * "to the end of the enclosing space".
 */
const readBoxAt = async (file: Blob, offset: number, limit: number): Promise<Box | null> => {
  if (offset + 8 > limit) return null;
  const view = await readView(file, offset, Math.min(offset + 16, limit));
  if (view.byteLength < 8) return null;
  const size32 = view.getUint32(0);
  const type = readFourCC(view, 4);
  if (!type) return null;

  let size: number | null;
  let headerSize = 8;
  if (size32 === 1) {
    if (view.byteLength < 16) return null;
    size = readUint64(view, 8);
    headerSize = 16;
  } else if (size32 === 0) {
    size = limit - offset;
  } else {
    size = size32;
  }
  if (size === null || size < headerSize || offset + size > limit) return null;
  return { type, start: offset, payloadStart: offset + headerSize, end: offset + size };
};

const childBoxes = async (file: Blob, parent: Box): Promise<Box[]> => {
  const boxes: Box[] = [];
  let offset = parent.payloadStart;
  while (offset + 8 <= parent.end && boxes.length < MAX_CHILD_BOXES) {
    const box = await readBoxAt(file, offset, parent.end);
    if (!box) break;
    boxes.push(box);
    offset = box.end;
  }
  return boxes;
};

const findChild = async (file: Blob, parent: Box | null | undefined, type: string): Promise<Box | null> => {
  if (!parent) return null;
  return (await childBoxes(file, parent)).find((box) => box.type === type) ?? null;
};

/** The top-level `moov` box, wherever it is (front or back of the file). */
const findMovieBox = async (file: Blob): Promise<Box | null> => {
  let offset = 0;
  for (let count = 0; count < MAX_TOP_LEVEL_BOXES && offset + 8 <= file.size; count += 1) {
    const box = await readBoxAt(file, offset, file.size);
    if (!box) return null;
    if (count === 0 && !LEADING_BOX_TYPES.has(box.type)) return null; // not ISO BMFF
    if (box.type === 'moov') return box;
    offset = box.end;
  }
  return null;
};

/**
 * Timescale and duration from an mvhd or mdhd box (same layout for both).
 * Duration is null when the file marks it unknown (all bits set).
 */
const readTimescaleAndDuration = async (
  file: Blob,
  box: Box
): Promise<{ timescale: number; duration: number | null } | null> => {
  const view = await readView(file, box.payloadStart, Math.min(box.payloadStart + 32, box.end));
  if (view.byteLength < 4) return null;
  const version = view.getUint8(0);
  if (version === 1) {
    if (view.byteLength < 32) return null;
    const timescale = view.getUint32(20);
    const allOnes = view.getUint32(24) === 0xffffffff && view.getUint32(28) === 0xffffffff;
    return { timescale, duration: allOnes ? null : readUint64(view, 24) };
  }
  if (view.byteLength < 20) return null;
  const timescale = view.getUint32(12);
  const duration = view.getUint32(16);
  return { timescale, duration: duration === 0xffffffff ? null : duration };
};

const readHandlerType = async (file: Blob, hdlr: Box): Promise<string | null> => {
  // version/flags (4), pre_defined - QuickTime's component type (4), handler type (4).
  if (hdlr.end - hdlr.payloadStart < 12) return null;
  const view = await readView(file, hdlr.payloadStart, hdlr.payloadStart + 12);
  return readFourCC(view, 8);
};

interface SampleTiming {
  samples: number;
  ticks: number;
  /** The frame duration (ticks) of the middle frame, counting every frame. */
  medianDelta: number;
}

/** Sample count, total duration and median frame duration from an stts box. */
const readSampleTiming = async (file: Blob, stts: Box): Promise<SampleTiming | null> => {
  const payloadLength = stts.end - stts.payloadStart;
  if (payloadLength < 8) return null;
  const head = await readView(file, stts.payloadStart, stts.payloadStart + 8);
  const entryCount = head.getUint32(4);
  const tableBytes = entryCount * 8;
  if (entryCount === 0 || tableBytes > MAX_STTS_BYTES || 8 + tableBytes > payloadLength) return null;

  const table = await readView(file, stts.payloadStart + 8, stts.payloadStart + 8 + tableBytes);
  let samples = 0;
  let ticks = 0;
  const countByDelta = new Map<number, number>();
  for (let offset = 0; offset < tableBytes; offset += 8) {
    const count = table.getUint32(offset);
    const delta = table.getUint32(offset + 4);
    // A zero delta (some muxers end on one) carries no timing information.
    if (count === 0 || delta === 0) continue;
    samples += count;
    ticks += count * delta;
    countByDelta.set(delta, (countByDelta.get(delta) ?? 0) + count);
  }
  if (samples === 0 || ticks === 0) return null;

  let medianDelta = 0;
  let seen = 0;
  for (const delta of Array.from(countByDelta.keys()).sort((a, b) => a - b)) {
    seen += countByDelta.get(delta)!;
    if (seen * 2 >= samples) {
      medianDelta = delta;
      break;
    }
  }
  return { samples, ticks, medianDelta };
};

/**
 * The standard rate of a video track, or null when `trak` is not usable
 * video or its timing matches no standard rate.
 */
const readVideoTrackRate = async (
  file: Blob,
  trak: Box,
  movieSeconds: number | null
): Promise<number | null> => {
  const mdia = await findChild(file, trak, 'mdia');
  if (!mdia) return null;
  const mdiaChildren = await childBoxes(file, mdia);
  const hdlr = mdiaChildren.find((box) => box.type === 'hdlr');
  if (!hdlr || (await readHandlerType(file, hdlr)) !== 'vide') return null;

  const mdhd = mdiaChildren.find((box) => box.type === 'mdhd');
  const media = mdhd ? await readTimescaleAndDuration(file, mdhd) : null;
  if (!media || media.timescale <= 0) return null;

  const minf = mdiaChildren.find((box) => box.type === 'minf');
  const stbl = await findChild(file, minf, 'stbl');
  const stts = await findChild(file, stbl, 'stts');
  if (!stts) return null;
  const timing = await readSampleTiming(file, stts);
  // One frame has no rate; a fragmented file keeps its samples elsewhere.
  if (!timing || timing.samples < 2) return null;

  // Sanity check against the movie header: a sample table that disagrees
  // wildly with the movie's length was misread, or describes something else.
  const mediaSeconds = timing.ticks / media.timescale;
  if (movieSeconds !== null && movieSeconds > 0) {
    const ratio = mediaSeconds / movieSeconds;
    if ((ratio < 0.5 || ratio > 2) && Math.abs(mediaSeconds - movieSeconds) > 1) return null;
  }

  // The weighted mean frame duration: exact for constant-rate files, and for
  // regular cadences such as 29.97 at a timescale of 600 (deltas of 20 with
  // a 21 every so often). Variable-rate footage with dropped or doubled
  // frames (screen recordings) averages to no standard rate at all; there
  // the median frame, like the playback measurement's median, gives the rate
  // the footage was recorded at.
  const meanRate = snapToStandardFrameRate((media.timescale * timing.samples) / timing.ticks);
  if (meanRate !== null) return meanRate;
  return snapToStandardFrameRate(media.timescale / timing.medianDelta);
};

/**
 * The frame rate recorded in an MP4 / MOV / M4V container, snapped to the
 * standard rate it encodes (exactly 23.976, 29.97, 59.94...), or null when
 * the file is not ISO BMFF, has no readable video track, or the rate is not
 * a standard one.
 */
export async function readContainerFrameRate(file: Blob): Promise<number | null> {
  try {
    const moov = await findMovieBox(file);
    if (!moov) return null;
    const moovChildren = await childBoxes(file, moov);

    const mvhd = moovChildren.find((box) => box.type === 'mvhd');
    const movie = mvhd ? await readTimescaleAndDuration(file, mvhd) : null;
    const movieSeconds = movie && movie.timescale > 0 && movie.duration !== null
      ? movie.duration / movie.timescale
      : null;

    for (const trak of moovChildren.filter((box) => box.type === 'trak')) {
      const rate = await readVideoTrackRate(file, trak, movieSeconds);
      if (rate !== null) return rate;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Pass 2: measure playback
// ---------------------------------------------------------------------------

type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    cb: (now: number, metadata: { mediaTime: number; presentedFrames: number }) => void
  ) => number;
};

function measurePlaybackFrameRate(file: File, sampleSeconds: number, timeoutMs: number): Promise<number | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video') as FrameCallbackVideo;
    if (typeof video.requestVideoFrameCallback !== 'function') {
      resolve(null);
      return;
    }

    const url = URL.createObjectURL(file);
    const intervals: number[] = [];
    let last: { mediaTime: number; presentedFrames: number } | null = null;
    let settled = false;

    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.pause();
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(value);
    };

    const onFrame = (_now: number, meta: { mediaTime: number; presentedFrames: number }) => {
      if (last) {
        const frames = meta.presentedFrames - last.presentedFrames;
        const dt = meta.mediaTime - last.mediaTime;
        if (frames > 0 && dt > 0) intervals.push(dt / frames);
      }
      last = meta;

      if (meta.mediaTime >= sampleSeconds || intervals.length >= 90) {
        if (intervals.length < 5) return finish(null);
        const sorted = [...intervals].sort((a, b) => a - b);
        const median = sorted[Math.floor(sorted.length / 2)];
        return finish(snapToStandardFrameRate(1 / median));
      }
      video.requestVideoFrameCallback!(onFrame);
    };

    const timer = window.setTimeout(() => finish(null), timeoutMs);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => finish(null);
    video.src = url;
    video.requestVideoFrameCallback(onFrame);
    video.play().catch(() => finish(null));
  });
}

/**
 * The file's frame rate: read from the container when it is MP4 / MOV / M4V,
 * otherwise measured by playing the start of the file. Null when unknown.
 */
export async function detectFrameRate(file: File, sampleSeconds = 1.2, timeoutMs = 4000): Promise<number | null> {
  const fromContainer = await readContainerFrameRate(file);
  if (fromContainer !== null) return fromContainer;
  return measurePlaybackFrameRate(file, sampleSeconds, timeoutMs);
}
